import { designVariables } from "@studio3t/design-variables";
import {
  BadgeSecondarySubtle,
  GenericDropdown,
  GenericDropdownContext,
  type GenericDropdownItem,
  GenericInput,
  Icon,
  IconSVG,
  InputType,
  type ListItemProps,
  StyledTableScroll,
  SVGColorKey,
  styledTableClassNames,
} from "@studio3t/generic-components";
import {
  type AggregationFn,
  type Column,
  type ColumnFiltersState,
  createColumnHelper,
  type ExpandedState,
  type FilterFn,
  flexRender,
  type GroupingState,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  type SortingFn,
  type SortingState,
  useReactTable,
  type VisibilityState,
} from "@tanstack/react-table";
import {
  type ElementType,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import styled, { css, useTheme } from "styled-components";
import {
  DROPDOWN_TRIGGER_ATTR,
  DropdownOpenReporter,
  useDropdownDismissGuard,
} from "@/components/dropdownDismissGuard";
import { JobStatusBadge } from "@/components/JobStatusBadge";
import { MoreTooltip } from "@/components/MoreTooltip";
import { ServiceStatusBadge } from "@/components/ServiceStatusBadge";
import { SkeletonBar, SkeletonPill } from "@/components/Skeleton";
import { SortableHeader as SharedSortableHeader } from "@/components/SortableHeader";
import {
  FilterBar,
  FilterControls,
  FilterInputWrap,
  FilterMultiSelect,
  FilterTrigger,
} from "@/components/TableFilters";
import type { QueryPhase } from "@/hooks/useApi";
import type { Service } from "@/types/api";
import { userStorage } from "@/utils/userStorage";
import { ActionsTrigger } from "../PipelineJobActions";
import {
  getHostPath,
  getProviderIcon,
  isLlmService,
  PROVIDER_LABELS,
  type ServiceStatusValue,
  type ServiceUsage,
  statusFor,
} from "./helpers";

export type { ServiceUsage } from "./helpers";

const SINGLE_LINE = designVariables.type.singleLine;
const SEMANTIC = designVariables.ui.semantic;
const BADGE = designVariables.ui.badge;
const BUTTON = designVariables.ui.button;
const TABLE = designVariables.ui.table;
const CARDS = designVariables.ui.cards;
const LIST = designVariables.ui.list;
const CARD_RADIUS = CARDS.corner;

// Minimum readable table width: Figma fixed columns (555 + 240 + 120 + 40 = 955)
// plus a floor for the Used-by column so its two entries' names stay readable.
// Below this the card scrolls horizontally instead of squashing columns.
const TABLE_MIN_WIDTH = 1420;

// Pipelines from this many onward collapse into a "+ N" counter (Figma shows two
// usages before the overflow).
const MAX_USED_BY = 2;

// Multi-select match: empty/absent selection matches everything.
function matchesAnyOf(value: string, filterValue: unknown): boolean {
  if (!Array.isArray(filterValue) || filterValue.length === 0) return true;
  return (filterValue as string[]).includes(value);
}

type StatusFilter = "live" | "idle";

// The column's data value adds "unknown" for while the jobs query is still
// loading or has errored — the cells render a skeleton/dash then, so the
// filter/sort layer must not treat those rows as "idle". "unknown" is not a
// user-facing filter option; it bypasses the Live/Idle filter entirely.
type StatusValue = ServiceStatusValue;

const statusIncludesAnyOf: FilterFn<Service> = (row, columnId, filterValue) => {
  const value = row.getValue<StatusValue>(columnId);
  return value === "unknown" || matchesAnyOf(value, filterValue);
};

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "live", label: "Live" },
  { value: "idle", label: "Idle" },
];

// Status sort priority: live (0) before idle (1). Rank 0 is live, so ascending
// (the default, down-arrow click) surfaces live services at the top and the up
// arrow flips to idle-first. Alphabetical ("idle" < "live") would disagree, so
// the explicit order keeps live-first the default. "unknown" (no jobs data
// yet) always sorts last.
const STATUS_SORT_ORDER: Record<StatusValue, number> = {
  live: 0,
  idle: 1,
  unknown: 2,
};

// Sorting reorders BOTH the provider group rows and the leaf rows within them.
// Two pieces make the group rows sort: an aggregationFn gives each group a
// representative value (its "smallest" leaf for the column) so the group lands
// where its first leaf would in a flat list — without it the group's getValue is
// undefined and TanStack skips it (leaving the groups unsorted) before the
// sortingFn ever runs. The sortingFn then compares those values, and because the
// aggregate is the leaf value's own type, the same comparator serves group and
// leaf rows. (Descending negates the comparator: the largest-keyed group leads,
// its leaves in descending order — the expected flat-sort feel.)

// Group representative: alphabetically-first leaf value (Name/Resources).
const aggregateMinString: AggregationFn<Service> = (columnId, leafRows) => {
  let min: string | null = null;
  for (const row of leafRows) {
    const value = row.getValue<string>(columnId) ?? "";
    if (min === null || value.localeCompare(min) < 0) min = value;
  }
  return min ?? "";
};

const sortByString: SortingFn<Service> = (rowA, rowB, columnId) =>
  rowA
    .getValue<string>(columnId)
    .localeCompare(rowB.getValue<string>(columnId));

// Group representative: the best-ranked leaf status, so a group with a live
// service sorts ahead of all-idle groups, and all-unknown groups sort last.
const aggregateStatus: AggregationFn<Service> = (columnId, leafRows) => {
  let best: StatusValue = "unknown";
  for (const row of leafRows) {
    const value = row.getValue<StatusValue>(columnId);
    if (STATUS_SORT_ORDER[value] < STATUS_SORT_ORDER[best]) best = value;
  }
  return best;
};

const sortByStatusPriority: SortingFn<Service> = (rowA, rowB, columnId) =>
  STATUS_SORT_ORDER[rowA.getValue<StatusValue>(columnId)] -
  STATUS_SORT_ORDER[rowB.getValue<StatusValue>(columnId)];

// Unlike the other columns, "Used by" sorts only the services WITHIN each provider
// group — the groups stay in place. That is the point of NOT giving this column an
// aggregationFn: a group row then has no usedBy value (getValue is undefined), so
// TanStack leaves the group rows unsorted and only orders the leaves. The
// getIsGrouped guard is belt-and-suspenders so the comparator never reads .length
// off that undefined group value. Ascending = least-used first; sorting by pipeline
// count answers "which services are most relied on" (alphabetising the pipeline
// names would have no operational meaning).
const sortByUsageCount: SortingFn<Service> = (rowA, rowB, columnId) => {
  if (rowA.getIsGrouped() || rowB.getIsGrouped()) return 0;
  return (
    rowA.getValue<string[]>(columnId).length -
    rowB.getValue<string[]>(columnId).length
  );
};

// The usedBy column's value is the list of pipeline names using the service. A
// service passes if it's used by ANY of the selected pipelines.
const usedByFilterFn: FilterFn<Service> = (row, columnId, filterValue) => {
  if (!Array.isArray(filterValue) || filterValue.length === 0) return true;
  const names = row.getValue<string[]>(columnId);
  return (filterValue as string[]).some((p) => names.includes(p));
};

// "Used by" filter: a height-capped, scrollable list of every pipeline assigned
// to a service. Selecting a pipeline narrows the table to the services it uses.
function UsedByFilter({
  values,
  pipelines,
  onChange,
  onOpenChange,
}: {
  values: string[];
  pipelines: string[];
  onChange: (values: string[]) => void;
  onOpenChange: (open: boolean) => void;
}) {
  const selected = new Set(values);
  const toggle = (v: string) =>
    onChange(
      values.includes(v) ? values.filter((x) => x !== v) : [...values, v],
    );

  const display =
    values.length === 0
      ? "Used by: All"
      : values.length === 1
        ? values[0]
        : `Used by (${values.length})`;

  const dropdownItems: GenericDropdownItem[] = [
    pipelines.map(
      (p): ListItemProps => ({
        id: p,
        type: "checkbox",
        label: p,
        value: selected.has(p),
        onClick: () => toggle(p),
      }),
    ),
  ];

  return (
    <GenericDropdown
      width={{ strategy: "fixed", size: 240 }}
      height={{ strategy: "max-height", size: 320 }}
      placement="bottom-end"
      dropdownItems={dropdownItems}
    >
      <GenericDropdownContext.Consumer>
        {({ open }) => (
          <FilterTrigger
            type="button"
            $active={open}
            {...DROPDOWN_TRIGGER_ATTR}
          >
            <DropdownOpenReporter onOpenChange={onOpenChange} />
            {display}
            <IconSVG
              icon={Icon.BaseChevron}
              size={10}
              svgColorKey={SVGColorKey.semanticTextBody}
            />
          </FilterTrigger>
        )}
      </GenericDropdownContext.Consumer>
    </GenericDropdown>
  );
}

// Adapts a TanStack column to the shared SortableHeader control (same affordance
// as the Pipelines list).
function SortableHeader({
  column,
  children,
}: {
  column: Column<Service, unknown>;
  children: ReactNode;
}) {
  return (
    <SharedSortableHeader
      direction={column.getIsSorted() || null}
      onClick={() => column.toggleSorting()}
    >
      {children}
    </SharedSortableHeader>
  );
}

// Mirrors the column's sort state onto the <th> so assistive tech can announce
// whether (and how) a column is sorted; undefined leaves aria-sort off the
// non-sortable columns entirely.
function ariaSortFor(
  column: Column<Service, unknown>,
): "ascending" | "descending" | "none" | undefined {
  if (!column.getCanSort()) return undefined;
  const sorted = column.getIsSorted();
  if (sorted === "asc") return "ascending";
  if (sorted === "desc") return "descending";
  return "none";
}

const columnHelper = createColumnHelper<Service>();

// Figma column widths (node 13692-123635): Name 555, Resources 240, Status 120,
// Used by flexes to fill the rest, delete 40.
function ColGroup() {
  return (
    <colgroup>
      <col style={{ width: 555 }} />
      <col style={{ width: 240 }} />
      <col style={{ width: 120 }} />
      <col />
      <col style={{ width: TABLE.fixwidth }} />
    </colgroup>
  );
}

function NameCell({ service }: { service: Service }) {
  const theme = useTheme();
  return (
    <NameWrap>
      <IconSVG
        icon={getProviderIcon(service.provider)}
        size={LIST.item.icon}
        colors={{ default: theme.semanticTextBody }}
      />
      <ServiceName>{service.name}</ServiceName>
      {service.is_system && <SystemBadge>System</SystemBadge>}
    </NameWrap>
  );
}

function ResourceCell({ service }: { service: Service }) {
  return (
    <ResourceWrap>
      <ResourceText>{getHostPath(service)}</ResourceText>
      {service.db_name && <ResourceDb>/ {service.db_name}</ResourceDb>}
    </ResourceWrap>
  );
}

function StatusCellContent({
  live,
  usagePhase,
}: {
  live: boolean;
  usagePhase: QueryPhase;
}) {
  // live/idle derives from the jobs query: skeleton while it loads (so no
  // false "idle" flash), an unknown dash if it failed — a skeleton there would
  // promise progress that isn't coming.
  if (usagePhase === "loading") {
    return (
      <StatusSlot>
        <SkeletonPill aria-hidden="true" />
      </StatusSlot>
    );
  }
  if (usagePhase === "error") {
    return <NotInUse>—</NotInUse>;
  }
  return (
    <StatusSlot>
      <ServiceStatusBadge live={live} />
    </StatusSlot>
  );
}

function UsedByCell({
  usage,
  usagePhase,
}: {
  usage: ServiceUsage[];
  usagePhase: QueryPhase;
}) {
  // Same jobs-derived data as Status: skeleton until the query resolves so
  // in-use services don't briefly read "Not in use", dash if it failed.
  if (usagePhase === "loading") {
    return <SkeletonBar $width="120px" aria-hidden="true" />;
  }
  if (usagePhase === "error") return <NotInUse>—</NotInUse>;
  if (usage.length === 0) return <NotInUse>Not in use</NotInUse>;
  const visible = usage.slice(0, MAX_USED_BY);
  const hidden = usage.slice(MAX_USED_BY);
  return (
    <UsedByList>
      {visible.map((u, idx) => (
        // A 1px rule separates each entry from the previous one (Figma).
        <UsedByItem key={u.name} $divided={idx > 0}>
          <UsedByName>{u.name}</UsedByName>
          <JobStatusBadge state={u.running ? "Running" : "Stopped"} />
        </UsedByItem>
      ))}
      {hidden.length > 0 && (
        // Hovering "+ N" reveals the remaining pipelines in a tooltip.
        <MoreTooltip heading="Pipelines" items={hidden.map((u) => u.name)}>
          <MoreCount>+ {hidden.length}</MoreCount>
        </MoreTooltip>
      )}
    </UsedByList>
  );
}

function DeleteCell({
  service,
  usage,
  usageReady,
  soleLlm = false,
  onDelete,
}: {
  service: Service;
  usage: ServiceUsage[];
  usageReady: boolean;
  // The only LLM service there is: the Agent and AI Helper fall back to it
  // whether or not the engine reports it active, so it is always in use.
  soleLlm?: boolean;
  onDelete: (name: string) => void;
}) {
  // Mirrors the Figma states: the bin is red and active only when the service is
  // safe to remove — not in use by a pipeline, not a system service, not the
  // currently active (or only) LLM. Otherwise it renders disabled (grey). Until
  // the jobs query has succeeded (loading OR failed), usage is unknown — keep
  // the bin disabled rather than offering to delete a service that may be in use.
  const canDelete =
    usageReady &&
    usage.length === 0 &&
    !service.is_system &&
    !service.is_active &&
    !soleLlm;
  return (
    <DeleteTrigger
      type="button"
      disabled={!canDelete}
      aria-label={`Delete ${service.name}`}
      onClick={() => onDelete(service.name)}
    >
      <IconSVG
        icon={Icon.Bin}
        size={BUTTON.iconsize}
        svgColorKey={
          canDelete
            ? SVGColorKey.errorIcon
            : SVGColorKey.semanticTextPlaceholder
        }
      />
    </DeleteTrigger>
  );
}

// LLM rows get the "…" menu (Figma DropdownMenu 16029-390241) in place of
// the bare bin once there is a choice of model (two or more LLM services):
// "Set as default" switches the Agent's and AI Helper's model to this
// service, Delete sits behind a separator. Both are disabled on the current
// default — the engine falls back to it, so it can't be re-selected or
// removed. The design's Edit item is not implemented yet.
function LlmActionsCell({
  service,
  onSetDefault,
  settingDefault,
  onDelete,
  onMenuOpenChange,
}: {
  service: Service;
  onSetDefault: (name: string) => void;
  // A switch is in flight: every row's "Set as default" waits for it, so two
  // activations can't race.
  settingDefault: boolean;
  onDelete: (name: string) => void;
  /** The menu's open state, for the table's dropdownDismissGuard. */
  onMenuOpenChange: (open: boolean) => void;
}) {
  const isDefault = service.is_active === true;
  const canDelete = !isDefault && !service.is_system;
  const items: GenericDropdownItem[] = [
    [
      {
        id: "set-default",
        type: "text",
        value: "set-default",
        label: "Set as default",
        leadingIcon: Icon.Anchor,
        disabled: isDefault || settingDefault,
        closeOnSelect: true,
        onClick: () => onSetDefault(service.name),
      },
    ],
    [
      {
        id: "delete",
        type: "text",
        value: "delete",
        label: "Delete",
        // Label stays the default colour like every other item; only the bin
        // is red (the ListItem `error` prop would make the row ignore clicks).
        leadingIcon: Icon.Bin,
        leadingIconColorKey: canDelete
          ? SVGColorKey.errorIcon
          : SVGColorKey.semanticTextPlaceholder,
        disabled: !canDelete,
        closeOnSelect: true,
        onClick: () => onDelete(service.name),
      },
    ],
  ];

  return (
    <GenericDropdown
      dropdownItems={items}
      width={{ strategy: "fixed", size: 202 }}
      placement="bottom-end"
    >
      <ActionsTrigger
        type="button"
        aria-label={`Actions for ${service.name}`}
        {...DROPDOWN_TRIGGER_ATTR}
      >
        <DropdownOpenReporter onOpenChange={onMenuOpenChange} />
        <IconSVG
          icon={Icon.MoreH}
          size={BUTTON.iconsize}
          svgColorKey={SVGColorKey.semanticTextBody}
        />
      </ActionsTrigger>
    </GenericDropdown>
  );
}

// The row's trailing action: the "…" menu for an LLM with alternatives,
// otherwise the plain bin.
function RowActions({
  service,
  llmCount,
  usage,
  usageReady,
  onDelete,
  onSetDefault,
  settingDefault,
  onMenuOpenChange,
}: {
  service: Service;
  llmCount: number;
  usage: ServiceUsage[];
  usageReady: boolean;
  onDelete: (name: string) => void;
  onSetDefault: (name: string) => void;
  settingDefault: boolean;
  onMenuOpenChange: (open: boolean) => void;
}) {
  const isLlm = isLlmService(service);
  // A lone LLM has nothing to switch to, so it keeps the plain bin
  // (disabled: it is the model in use).
  if (isLlm && llmCount > 1) {
    return (
      <LlmActionsCell
        service={service}
        onSetDefault={onSetDefault}
        settingDefault={settingDefault}
        onDelete={onDelete}
        onMenuOpenChange={onMenuOpenChange}
      />
    );
  }
  return (
    <DeleteCell
      service={service}
      usage={usage}
      usageReady={usageReady}
      soleLlm={isLlm}
      onDelete={onDelete}
    />
  );
}

// Filters, sort, and group expand/collapse survive a page refresh (per user),
// mirroring the Pipelines list's persistence.
const TABLE_SETTINGS_KEY = "services-table";

// Column ids that support sorting — guards against stale storage referencing the
// non-sortable "provider"/"actions" columns.
const SORTABLE_COLUMN_IDS = ["name", "resources", "status", "usedBy"];

interface StoredTableSettings {
  name?: string;
  status?: StatusFilter[];
  usedBy?: string[];
  sort?: SortingState;
  expanded?: ExpandedState;
}

function parseSort(value: unknown): SortingState | undefined {
  if (!Array.isArray(value)) return undefined;
  const sort = value.filter(
    (s): s is { id: string; desc: boolean } =>
      typeof s === "object" &&
      s !== null &&
      typeof (s as { id?: unknown }).id === "string" &&
      SORTABLE_COLUMN_IDS.includes((s as { id: string }).id) &&
      typeof (s as { desc?: unknown }).desc === "boolean",
  );
  return sort.length ? sort : undefined;
}

// Keeps only values that are still valid options (storage may hold stale data).
function parseFilterValues<T extends string>(
  value: unknown,
  options: { value: T; label: string }[],
): T[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const allowed = options.map((o) => o.value);
  return value.filter((v): v is T => allowed.includes(v as T));
}

// Used-by holds arbitrary pipeline names (no fixed option list); keep the strings
// here and let the component prune any that no longer exist against live data.
function parseStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.filter((v): v is string => typeof v === "string");
}

// A non-null, non-array object — i.e. a stored settings record or expanded-state
// map (JSON.parse can also yield primitives or arrays).
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

// Expanded is either `true` (every group open — the default) or a per-group-id
// map of open/closed flags.
function parseExpanded(value: unknown): ExpandedState | undefined {
  if (value === true) return true;
  if (!isPlainObject(value)) return undefined;
  const entries = Object.values(value);
  if (!entries.every((v) => typeof v === "boolean")) return undefined;
  return value as Record<string, boolean>;
}

function loadTableSettings(): StoredTableSettings {
  try {
    const raw = userStorage.getItem(TABLE_SETTINGS_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!isPlainObject(parsed)) return {};
    const stored = parsed;
    return {
      name: typeof stored.name === "string" ? stored.name : undefined,
      status: parseFilterValues(stored.status, STATUS_OPTIONS),
      usedBy: parseStringArray(stored.usedBy),
      sort: parseSort(stored.sort),
      expanded: parseExpanded(stored.expanded),
    };
  } catch {
    return {};
  }
}

function saveTableSettings(settings: StoredTableSettings): void {
  userStorage.setItem(TABLE_SETTINGS_KEY, JSON.stringify(settings));
}

function initialColumnFilters(stored: StoredTableSettings): ColumnFiltersState {
  const filters: ColumnFiltersState = [];
  if (stored.name) filters.push({ id: "name", value: stored.name });
  if (stored.status?.length) {
    filters.push({ id: "status", value: stored.status });
  }
  // Used-by is restored verbatim here, NOT pruned at load: the pipeline names come
  // from the jobs query, which can still be loading at this point, so pruning now
  // would drop valid selections (and the save-effect would persist the loss).
  // Pruning of genuinely-deleted pipelines happens in the component's reconcile
  // effect, which waits until the pipeline data has actually loaded.
  if (stored.usedBy?.length) {
    filters.push({ id: "usedBy", value: stored.usedBy });
  }
  return filters;
}

// Skeleton body shown while the services query loads. Uses the same row/cell
// components as the data rows so column widths and padding match exactly; the
// bar shapes echo each column's content (text, text, badge, text, nothing).
const SKELETON_ROW_COUNT = 8;
const SKELETON_ROWS = Array.from(
  { length: SKELETON_ROW_COUNT },
  (_, i) => `skeleton-${i}`,
);

function SkeletonBodyRows() {
  return (
    <>
      {SKELETON_ROWS.map((key) => (
        <TableRow key={key} aria-hidden="true">
          <NameTableCell>
            <SkeletonBar $width="45%" />
          </NameTableCell>
          <TableCell>
            <SkeletonBar $width="60%" />
          </TableCell>
          <StatusCell>
            <SkeletonPill />
          </StatusCell>
          <TableCell>
            <SkeletonBar $width="50%" />
          </TableCell>
          <ActionsCell />
        </TableRow>
      ))}
    </>
  );
}

export function ServicesTable({
  services,
  usageByService,
  usagePhase,
  loading = false,
  onRowClick,
  onDelete,
  onSetDefault,
  settingDefault,
}: {
  services: Service[];
  usageByService: Record<string, ServiceUsage[]>;
  // Lifecycle of the jobs query backing `usageByService`: the Status/Used-by
  // cells skeleton while "loading" and show an unknown dash on "error" (instead
  // of spinning forever if the query fails). The Used-by reconcile effect waits
  // for "ready" before pruning deleted pipelines, so an incomplete pipeline set
  // can't drop a valid persisted filter.
  usagePhase: QueryPhase;
  // True while the services query itself is loading: the body renders skeleton
  // rows instead of data rows (which don't exist yet).
  loading?: boolean;
  onRowClick: (name: string) => void;
  onDelete: (name: string) => void;
  // LLM rows only: make this service the Agent's / AI Helper's model, and
  // whether such a switch is in flight (the menus' "Set as default" waits).
  onSetDefault: (name: string) => void;
  settingDefault: boolean;
}) {
  const usageReady = usagePhase === "ready";
  // A click that closes a filter or row menu does nothing else (no row
  // opens, no group toggles).
  const dismissGuard = useDropdownDismissGuard<HTMLDivElement>();
  const onMenuOpenChange = dismissGuard.onDropdownOpenChange;
  const [stored] = useState(loadTableSettings);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(() =>
    initialColumnFilters(stored),
  );
  const [sorting, setSorting] = useState<SortingState>(() => stored.sort ?? []);
  const [expanded, setExpanded] = useState<ExpandedState>(
    () => stored.expanded ?? true,
  );
  const [grouping] = useState<GroupingState>(["provider"]);
  const [columnVisibility] = useState<VisibilityState>({ provider: false });

  const usageFor = useCallback(
    (name: string): ServiceUsage[] => usageByService[name] ?? [],
    [usageByService],
  );

  // How many LLM services there are: the actions cell offers the "…" menu
  // (with "Set as default") only once there is more than one to pick from.
  const llmCount = useMemo(
    () => services.filter(isLlmService).length,
    [services],
  );

  const columns = useMemo(
    () => [
      // Grouping-only column: hidden in the body (its values drive the group
      // headers we render manually).
      columnHelper.accessor("provider", {
        id: "provider",
        header: "Provider",
        enableSorting: false,
      }),
      columnHelper.accessor("name", {
        id: "name",
        header: "Name",
        filterFn: "includesString",
        aggregationFn: aggregateMinString,
        sortingFn: sortByString,
        cell: (info) => <NameCell service={info.row.original} />,
      }),
      columnHelper.accessor((s) => getHostPath(s), {
        id: "resources",
        header: "Resources",
        aggregationFn: aggregateMinString,
        sortingFn: sortByString,
        cell: (info) => <ResourceCell service={info.row.original} />,
      }),
      columnHelper.accessor(
        (s): StatusValue => statusFor(s, usageFor(s.name), usageReady),
        {
          id: "status",
          header: "Status",
          filterFn: statusIncludesAnyOf,
          aggregationFn: aggregateStatus,
          sortingFn: sortByStatusPriority,
          cell: (info) => (
            <StatusCellContent
              live={info.getValue() === "live"}
              // LLM status is part of the services response itself, so it
              // never waits on the jobs query.
              usagePhase={
                isLlmService(info.row.original) ? "ready" : usagePhase
              }
            />
          ),
        },
      ),
      columnHelper.accessor((s) => usageFor(s.name).map((u) => u.name), {
        id: "usedBy",
        header: "Used by",
        filterFn: usedByFilterFn,
        // No aggregationFn: keeps the provider groups in place and sorts only the
        // services within each group (see sortByUsageCount).
        sortingFn: sortByUsageCount,
        // The other columns sort ascending on first click; TanStack would default
        // this count-based column to descending-first, so pin it for consistency.
        sortDescFirst: false,
        cell: (info) =>
          // Pipelines can't reference LLM services, so "Not in use" would be
          // misleading there — the cell stays empty for them.
          isLlmService(info.row.original) ? null : (
            <UsedByCell
              usage={usageFor(info.row.original.name)}
              usagePhase={usagePhase}
            />
          ),
      }),
      columnHelper.display({
        id: "actions",
        header: () => null,
        cell: (info) => (
          <RowActions
            service={info.row.original}
            llmCount={llmCount}
            usage={usageFor(info.row.original.name)}
            usageReady={usageReady}
            onDelete={onDelete}
            onSetDefault={onSetDefault}
            settingDefault={settingDefault}
            onMenuOpenChange={onMenuOpenChange}
          />
        ),
      }),
    ],
    [
      usageFor,
      usagePhase,
      usageReady,
      llmCount,
      onDelete,
      onSetDefault,
      settingDefault,
      onMenuOpenChange,
    ],
  );

  const table = useReactTable({
    data: services,
    columns,
    state: { columnFilters, sorting, expanded, grouping, columnVisibility },
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowId: (service) => service.name,
    autoResetExpanded: false,
  });

  const nameFilter =
    (table.getColumn("name")?.getFilterValue() as string) ?? "";
  const statusFilter =
    (table.getColumn("status")?.getFilterValue() as StatusFilter[]) ?? [];
  const usedByFilter =
    (table.getColumn("usedBy")?.getFilterValue() as string[]) ?? [];

  // Every distinct pipeline assigned to any service, for the Used-by filter's
  // pipeline list.
  const allPipelines = useMemo(() => {
    const set = new Set<string>();
    for (const usage of Object.values(usageByService)) {
      for (const u of usage) set.add(u.name);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [usageByService]);

  // Once the pipeline data has loaded, drop any Used-by selection whose pipeline
  // no longer exists (e.g. it was deleted since the filter was persisted). Gated
  // on usageReady so the still-loading pipeline set can't wipe a valid selection;
  // the resulting filter change is persisted by the effect below.
  useEffect(() => {
    if (!usageReady || usedByFilter.length === 0) return;
    const valid = new Set(allPipelines);
    const pruned = usedByFilter.filter((p) => valid.has(p));
    if (pruned.length !== usedByFilter.length) {
      table
        .getColumn("usedBy")
        ?.setFilterValue(pruned.length ? pruned : undefined);
    }
  }, [usageReady, allPipelines, usedByFilter, table]);

  // Persist filters, sort, and expand/collapse whenever they change (per user).
  useEffect(() => {
    const filterValue = (id: string) =>
      columnFilters.find((f) => f.id === id)?.value;
    saveTableSettings({
      name: (filterValue("name") as string) ?? "",
      status: (filterValue("status") as StatusFilter[]) ?? [],
      usedBy: (filterValue("usedBy") as string[]) ?? [],
      sort: sorting,
      expanded,
    });
  }, [columnFilters, sorting, expanded]);

  const setFilter = (columnId: string, value: unknown) => {
    table.getColumn(columnId)?.setFilterValue(value);
  };

  const rows = table.getRowModel().rows;
  const leafCount = table.getVisibleLeafColumns().length;
  const hasMatches = rows.length > 0;

  return (
    <TableCard
      ref={dismissGuard.containerRef}
      onClickCapture={dismissGuard.onClickCapture}
    >
      <FilterBar>
        <FilterInputWrap>
          <GenericInput
            type={InputType.Text}
            value={nameFilter}
            placeholder="Filter by name"
            onChange={(e) => setFilter("name", e.target.value || undefined)}
            allowClear={nameFilter.length > 0}
            onClear={() => setFilter("name", undefined)}
          />
        </FilterInputWrap>
        <FilterControls>
          <FilterMultiSelect
            placeholder="Status"
            values={statusFilter}
            options={STATUS_OPTIONS}
            onChange={(v) => setFilter("status", v.length ? v : undefined)}
            onOpenChange={dismissGuard.onDropdownOpenChange}
          />
          <UsedByFilter
            values={usedByFilter}
            pipelines={allPipelines}
            onChange={(v) => setFilter("usedBy", v.length ? v : undefined)}
            onOpenChange={dismissGuard.onDropdownOpenChange}
          />
        </FilterControls>
      </FilterBar>

      <StyledTableScroll>
        <Table>
          <ColGroup />
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers
                  .filter((header) => header.column.id !== "provider")
                  .map((header) => (
                    <TableHeader
                      key={header.id}
                      aria-sort={ariaSortFor(header.column)}
                      aria-label={
                        header.column.id === "actions" ? "Actions" : undefined
                      }
                    >
                      {header.column.getCanSort() ? (
                        <SortableHeader column={header.column}>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </SortableHeader>
                      ) : (
                        flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )
                      )}
                    </TableHeader>
                  ))}
              </tr>
            ))}
          </TableHead>
          <tbody>
            {loading && <SkeletonBodyRows />}
            {!loading &&
              rows.map((row) => {
                if (row.getIsGrouped()) {
                  const provider = row.getGroupingValue(
                    "provider",
                  ) as Service["provider"];
                  const toggleExpanded = row.getToggleExpandedHandler();
                  return (
                    <GroupRow
                      key={row.id}
                      tabIndex={0}
                      aria-expanded={row.getIsExpanded()}
                      onClick={toggleExpanded}
                      onKeyDown={(e) => {
                        if (e.target !== e.currentTarget) return;
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggleExpanded();
                        }
                      }}
                    >
                      <GroupCell colSpan={leafCount}>
                        <GroupContent>
                          <GroupChevron $expanded={row.getIsExpanded()}>
                            <IconSVG
                              icon={Icon.Chevron}
                              size={LIST.item.chevron}
                              svgColorKey={SVGColorKey.semanticTextBody}
                            />
                          </GroupChevron>
                          <GroupLabel>
                            {PROVIDER_LABELS[provider] ?? provider}
                          </GroupLabel>
                          <BadgeSecondarySubtle>
                            {row.subRows.length}
                          </BadgeSecondarySubtle>
                        </GroupContent>
                      </GroupCell>
                    </GroupRow>
                  );
                }
                return (
                  <TableRow
                    key={row.id}
                    tabIndex={0}
                    onClick={() => onRowClick(row.original.name)}
                    onKeyDown={(e) => {
                      if (e.target !== e.currentTarget) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onRowClick(row.original.name);
                      }
                    }}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const Cell = CELL_COMPONENTS[cell.column.id] ?? TableCell;
                      return (
                        <Cell
                          key={cell.id}
                          onClick={
                            cell.column.id === "actions"
                              ? (e: ReactMouseEvent) => e.stopPropagation()
                              : undefined
                          }
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </Cell>
                      );
                    })}
                  </TableRow>
                );
              })}
          </tbody>
        </Table>

        {!loading && !hasMatches && (
          <EmptyRowMessage>
            {services.length === 0
              ? "You have not added any service yet"
              : "No services match your filters"}
          </EmptyRowMessage>
        )}
      </StyledTableScroll>
    </TableCard>
  );
}

// Same card layout as the Pipelines list: the card fills the page and the
// StyledTableScroll inside it owns both scrollbars, so the filter bar stays
// visible while the rows scroll beneath the sticky column header.
const TableCard = styled.div`
  background: ${(props) => props.theme.cards1Background};
  border: 1px solid ${(props) => props.theme.cards1Border};
  border-radius: ${CARD_RADIUS}px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;

  /* StyledTableScroll's frame draws its own border and stretches to 100%
   * height; the card already owns the border, and the frame should hug the
   * rows so a short table doesn't leave a gap below the last row. */
  .${styledTableClassNames.scroll} {
    border: none;
    height: auto;
    min-height: 0;
  }

  /* Keep the vertical scrollbar below the sticky header row. !important is
   * required to beat overlayscrollbars' own positioning rules (same as
   * common-ui's DataTable does). */
  .os-scrollbar-vertical {
    top: ${TABLE.thead.cell.height + 1}px !important;
  }
`;

const Table = styled.table`
  width: 100%;
  /* Below TABLE_MIN_WIDTH the card scrolls horizontally instead of squashing
     columns; above it Used-by grows to fill. */
  min-width: ${TABLE_MIN_WIDTH}px;
  /* separate, not collapse: collapsed borders detach from position: sticky
     headers and scroll away with the body */
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
  background: ${(props) => props.theme.cards1Background};

  th:last-child,
  td:last-child {
    border-right: none;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`;

const TableHead = styled.thead`
  th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: ${(props) => props.theme.cards1Background};
  }
`;

const TableHeader = styled.th`
  text-align: left;
  height: ${TABLE.thead.cell.height}px;
  padding: 0 ${TABLE.thead.cell.paddingr}px 0 ${TABLE.thead.cell.paddingl}px;
  font-size: ${SINGLE_LINE.small.regular.fontSize}px;
  font-weight: ${SINGLE_LINE.small.regular.fontWeight};
  color: ${(props) => props.theme.semanticTextLight};
  white-space: nowrap;
  border-bottom: 1px solid ${(props) => props.theme.tableBackground};
  border-right: 1px solid ${(props) => props.theme.tableBackground};

  /* Column separators are the gaps *between* columns (Figma 13349-39133):
     the last column meets the card edge without its own line, which would
     double with the card border. */
  &:last-child {
    border-right: none;
  }
`;

const TableRow = styled.tr`
  cursor: pointer;

  &:hover {
    background: ${(props) => props.theme.tableTbodyCellHoverBackground};
  }

  &:focus-visible {
    outline-offset: -${SEMANTIC.focusstroke}px;
  }
`;

const TableCell = styled.td`
  height: ${TABLE.tbody.cell.heightm}px;
  padding: 0 ${TABLE.tbody.cell.padding}px;
  font-size: ${SINGLE_LINE.default.regular.fontSize}px;
  color: ${(props) => props.theme.semanticTextBody};
  vertical-align: middle;
  border-bottom: 1px solid ${(props) => props.theme.tableBackground};
  border-right: 1px solid ${(props) => props.theme.tableBackground};
  overflow: hidden;
`;

const StatusCell = styled(TableCell)``;

const ActionsCell = styled.td`
  height: ${TABLE.tbody.cell.heightm}px;
  padding: 0 ${TABLE.tbody.cell.padding}px;
  text-align: center;
  vertical-align: middle;
  border-bottom: 1px solid ${(props) => props.theme.tableBackground};
`;

// Group header row (per provider). Spans the full table width.
const GroupRow = styled.tr`
  cursor: pointer;

  &:hover {
    background: ${(props) => props.theme.tableTbodyCellHoverBackground};
  }

  &:focus-visible {
    outline-offset: -${SEMANTIC.focusstroke}px;
  }
`;

const GroupCell = styled.td`
  height: ${TABLE.tbody.cell.heightm}px;
  padding: 0 ${TABLE.tbody.cell.padding}px;
  border-bottom: 1px solid ${(props) => props.theme.tableBackground};
  background: ${(props) => props.theme.cards1Background};
`;

const GroupContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${TABLE.tbody.cell.gap}px;
  padding: 0 ${TABLE.tbody.cell.gap}px;
`;

// Chevron points right by default (per Figma): rotate 90deg to point down when
// the group is expanded, leave it pointing right when collapsed.
const GroupChevron = styled.span<{ $expanded: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${LIST.item.chevron}px;
  transition: transform 0.15s ease;
  transform: rotate(${(props) => (props.$expanded ? 90 : 0)}deg);
`;

const GroupLabel = styled.span`
  font-size: ${SINGLE_LINE.default.regular.fontSize}px;
  font-weight: ${SINGLE_LINE.default.regular.fontWeight};
  color: ${(props) => props.theme.semanticTextBody};
  white-space: nowrap;
`;

// First-column leaf cell: indent past the group label (Figma
// Table/tbody/cell/indent1) so the service icon sits to the right of its
// provider group's name, then the standard cell gap inside. The indent lives on
// the <td> so the flex wrap keeps the same px-6 gap as every other cell's fill
// item.
const NameTableCell = styled(TableCell)`
  padding-left: ${TABLE.tbody.cell.indent1}px;
`;

const NameWrap = styled.div`
  display: flex;
  align-items: center;
  gap: ${TABLE.tbody.cell.gap}px;
  padding: 0 ${TABLE.tbody.cell.gap}px;
  min-width: 0;
`;

const ServiceName = styled.span`
  font-weight: ${SINGLE_LINE.default.regular.fontWeight};
  color: ${(props) => props.theme.semanticTextBody};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SystemBadge = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  height: ${BADGE.height}px;
  padding: 0 ${BADGE.paddingw}px;
  border-radius: ${BADGE.corner}px;
  font-size: ${SINGLE_LINE.small.medium.fontSize}px;
  font-weight: ${SINGLE_LINE.small.medium.fontWeight};
  color: ${(props) => props.theme.semanticTextLight};
  background: ${(props) => props.theme.tableTheadCellBackground};
`;

const ResourceWrap = styled.div`
  display: flex;
  align-items: center;
  gap: ${TABLE.tbody.cell.gap}px;
  padding: 0 ${TABLE.tbody.cell.gap}px;
  min-width: 0;
`;

const ResourceText = styled.span`
  color: ${(props) => props.theme.semanticTextBody};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// The db segment never yields to host pressure (a short "/ db" stays whole
// while a long host ellipsizes), but its own length is capped so a long
// database name ellipsizes inside the cell instead of spilling into the
// Status column — the cap leaves room for the (usually masked) host prefix.
const ResourceDb = styled.span`
  color: ${(props) => props.theme.semanticTextLight};
  flex-shrink: 0;
  max-width: calc(100% - 56px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StatusSlot = styled.div`
  padding-left: ${TABLE.tbody.cell.gap}px;

  & > * {
    display: inline-flex;
  }
`;

// Stays on a single line (no wrapping → uniform row height). When the column is
// tight, the pipeline names ellipsize first (see UsedByName) while the status
// badges and "+ N" counter stay intact; the table only scrolls horizontally once
// the fixed columns themselves no longer fit.
const UsedByList = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: ${TABLE.tbody.cell.gap}px;
  padding: 0 ${TABLE.tbody.cell.gap}px;
  min-width: 0;
  overflow: hidden;
`;

const UsedByItem = styled.span<{ $divided?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${BADGE.gap}px;
  /* Lets the item shrink so its name can ellipsize. */
  min-width: 0;

  /* Protect the status badge — only the name (first child) gives up width. */
  & > *:last-child {
    flex-shrink: 0;
  }

  ${(props) =>
    props.$divided &&
    css`
      padding-left: ${TABLE.tbody.cell.gap}px;
      border-left: 1px solid ${props.theme.semanticBorderDark};
    `}
`;

// Sized to its content for short names (so the status badge sits right next to
// it), capped at ~28 chars, and allowed to ellipsize (min-width: 0) when the
// column is tight. The Used-by column's own floor (see Table min-width) keeps
// names from ever shrinking to an unreadable width before the table scrolls.
const UsedByName = styled.span`
  flex: 0 1 auto;
  min-width: 0;
  max-width: 28ch;
  font-size: ${SINGLE_LINE.default.regular.fontSize}px;
  color: ${(props) => props.theme.semanticTextBody};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NotInUse = styled.span`
  padding: 0 ${TABLE.tbody.cell.gap}px;
  font-size: ${SINGLE_LINE.default.regular.fontSize}px;
  font-style: italic;
  color: ${(props) => props.theme.semanticTextLight};
`;

// The "+ N" overflow counter; hovering it shows the remaining pipelines in a
// tooltip (see MoreTooltip).
const MoreCount = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding-left: ${TABLE.tbody.cell.gap}px;
  border-left: 1px solid ${(props) => props.theme.semanticBorderDark};
  font-size: ${SINGLE_LINE.small.medium.fontSize}px;
  font-weight: ${SINGLE_LINE.small.medium.fontWeight};
  white-space: nowrap;
  color: ${(props) => props.theme.semanticTextPlaceholder};
  transition: color 0.15s ease;

  &:hover {
    color: ${(props) => props.theme.semanticTextBody};
  }
`;

const DeleteTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: ${BUTTON.s.size}px;
  padding: 0 ${BUTTON.s.paddingw}px;
  border: none;
  border-radius: ${BUTTON.corner}px;
  background: transparent;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: ${(props) => props.theme.sidebarVerticalTabsItemHoverBackground};
  }

  &:disabled {
    cursor: default;
  }

  &:focus-visible {
    outline: 5px auto -webkit-focus-ring-color;
  }
`;

const EmptyRowMessage = styled.div`
  padding: 40px 0;
  text-align: center;
  font-size: ${SINGLE_LINE.small.regular.fontSize}px;
  font-style: italic;
  color: ${(props) => props.theme.semanticTextLight};
`;

// Columns whose <td> differs from the default TableCell. Declared after the
// styled components it references so they're initialised before this runs.
const CELL_COMPONENTS: Record<string, ElementType> = {
  name: NameTableCell,
  status: StatusCell,
  actions: ActionsCell,
};
